import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { RunbookTable } from '@/features/objects/components/RunbookTable'
import { FeatheredScrollArea } from '@/components/ui/feathered-scroll-area'
import { useRunbook } from '@/features/objects/hooks'
import { calculateRunbookTimes } from '@/features/objects/utils/calculateRunbookTimes'
import type { RunbookTask } from '@/types/entities'

interface RunbookViewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string
    scenario: number | null
    forecastStartTime: string | Date | null
}

export function RunbookViewDialog({
    open,
    onOpenChange,
    objectID,
    scenario,
    forecastStartTime,
}: RunbookViewDialogProps) {
    const { t } = useTranslation()
    const { data: runbookTasks, isLoading } = useRunbook(objectID, scenario !== null && scenario !== undefined ? scenario.toString() : undefined)
    const [displayTasks, setDisplayTasks] = useState<RunbookTask[]>([])

    // Calculate times based on forecast start time
    useEffect(() => {
        if (runbookTasks && forecastStartTime) {
            const baseTime = forecastStartTime instanceof Date 
                ? forecastStartTime 
                : new Date(forecastStartTime)
            
            // First calculate TaskOffset
            const tasksWithOffsets = calculateRunbookTimes(runbookTasks)
            
            // Then calculate StartTime and FinishTime from TaskOffset using baseTime
            const tasksWithTimes = tasksWithOffsets.map(task => {
                let startTime: Date | null = null
                let finishTime: Date | null = null
                
                if (task.taskOffset !== null && task.taskOffset !== undefined && task.duration !== null && task.duration !== undefined) {
                    startTime = new Date(baseTime.getTime() + task.taskOffset * 60 * 1000)
                    finishTime = new Date(startTime.getTime() + task.duration * 60 * 1000)
                }
                
                return {
                    ...task,
                    startTime,
                    finishTime,
                }
            })
            
            setDisplayTasks(tasksWithTimes)
        } else if (runbookTasks) {
            // If no forecast start time, just calculate offsets
            setDisplayTasks(calculateRunbookTimes(runbookTasks))
        } else {
            setDisplayTasks([])
        }
    }, [runbookTasks, forecastStartTime])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-hidden">
                <DialogHeader>
                    <DialogTitle>
                        {t('recoverRequests.runbook')} - {objectID}
                    </DialogTitle>
                    <DialogDescription>
                        {t('recoverRequests.runbookDescription')}
                    </DialogDescription>
                </DialogHeader>
                <div className="mt-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="text-muted-foreground">{t('recoverRequests.loadingRunbook')}</div>
                        </div>
                    ) : displayTasks.length === 0 ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="text-muted-foreground">{t('recoverRequests.noRunbook')}</div>
                        </div>
                    ) : (
                        <FeatheredScrollArea viewportClassName="max-h-[75vh]">
                            <RunbookTable
                                tasks={displayTasks}
                                onSave={() => {}}
                                autoUpdate={false}
                                onAutoUpdateChange={() => {}}
                                hideToolbar={true}
                                readOnly={true}
                            />
                        </FeatheredScrollArea>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
